import React, { useState, useMemo, useContext } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import InputGroup from 'react-bootstrap/InputGroup'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faColumns,
  faGripVertical,
  faSearch,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faFilter
} from '@fortawesome/free-solid-svg-icons'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTableColumns } from 'api/hooks/use-table-columns'
import { useAnalyticsColumns } from 'api/hooks/use-analytics-columns'
import { useReorderDashboardColumns } from 'api/hooks/use-dashboard-columns'
import { useQueryClient, useQuery, useMutation } from 'react-query'
import UserContext from 'context/user-context'
import UpdateColumn from './update-column'
import UpdateAnalyticsColumn from './update-analytics-column'
import CustomColumnsSection from './custom-columns-section'
import customColumnService from '../services/customColumnService'
import AuthContext from 'context/auth-context'
import {
  getColumnDisplayName,
  compareColumnsForDisplay,
  isColumnFiltered,
  isAnalyticsColumnFiltered,
  isCustomColumnFiltered
} from './utils'
import { useActiveAccountGrouping } from 'api/hooks/use-account-groupings'
import {
  useCreateDashboardColumn,
  useDeleteDashboardColumn
} from 'api/hooks/use-dashboard-columns'
import {
  useCreateDashboardAnalyticsColumn,
  useDeleteDashboardAnalyticsColumn,
  useReorderDashboardAnalyticsColumns
} from 'api/hooks/use-dashboard-analytics-columns'

// Sortable column item component for drag and drop
function SortableColumnItem({
  id,
  column,
  municipality,
  groupingName,
  onEditCustomColumn,
  onDeleteCustomColumn,
  editingCustomColumnId,
  editingCustomColumnName,
  setEditingCustomColumnName,
  onSaveEditCustomColumn,
  onCancelEditCustomColumn,
  disabled = false,
  params,
  isUserCreator
}) {
  const isThirdLineId = column.is_row_id
  const isAnalytics = column.is_analytics
  const isFiltered = column.is_custom
    ? isCustomColumnFiltered(column, params)
    : column.is_analytics
    ? isAnalyticsColumnFiltered(column, params)
    : isColumnFiltered(column, params)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id,
    disabled: isThirdLineId || disabled
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    cursor: isThirdLineId || disabled ? 'not-allowed' : 'default'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`d-flex align-items-center mb-2 p-2 border rounded ${
        disabled ? 'bg-light' : 'bg-white'
      }`}
    >
      {!isThirdLineId && !disabled ? (
        <span
          {...listeners}
          className="drag-handle me-2 text-primary"
          style={{ cursor: 'grab' }}
          title="Drag to reorder column"
        >
          <FontAwesomeIcon icon={faGripVertical} />
        </span>
      ) : (
        <span
          className="me-2 d-inline-block text-muted"
          style={{ width: '16px' }}
          title={
            isThirdLineId
              ? 'ThirdLine ID must remain as the first column'
              : 'Column reordering is disabled for non-owners'
          }
        >
          {/* Empty space to maintain alignment */}
        </span>
      )}
      <div className="flex-grow-1 d-flex align-items-center">
        {column.is_custom && editingCustomColumnId === column.id ? (
          <InputGroup size="sm" className="me-2">
            <Form.Control
              type="text"
              value={editingCustomColumnName}
              onChange={(e) => setEditingCustomColumnName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  onSaveEditCustomColumn()
                } else if (e.key === 'Escape') {
                  onCancelEditCustomColumn()
                }
              }}
              autoFocus
            />
            <Button
              variant="success"
              size="sm"
              onClick={onSaveEditCustomColumn}
              disabled={!editingCustomColumnName.trim()}
            >
              <FontAwesomeIcon icon={faCheck} />
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onCancelEditCustomColumn}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </InputGroup>
        ) : (
          <>
            <span className="fw-medium">
              {getColumnDisplayName(column, municipality, groupingName)}
            </span>
            {isThirdLineId && (
              <span className="text-muted small ms-2">(fixed position)</span>
            )}
            {column.is_custom && (
              <span className="badge badge-primary ms-2">Custom</span>
            )}
            {isAnalytics && (
              <span className="badge badge-info ms-2">Analytics</span>
            )}
            {isFiltered && (
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    This column has a filter applied. Filtered columns cannot be
                    removed from the view
                  </Tooltip>
                }
              >
                <div
                  className="badge badge-secondary ms-2"
                  style={{ cursor: 'help' }}
                >
                  <FontAwesomeIcon icon={faFilter} /> Filtered
                </div>
              </OverlayTrigger>
            )}
          </>
        )}
      </div>
      {column.is_custom && editingCustomColumnId !== column.id && !disabled && (
        <div className="ms-2 d-flex align-items-center">
          <button
            type="button"
            className="btn btn-link btn-sm text-primary p-1 me-1"
            onClick={() => onEditCustomColumn(column)}
            title="Edit custom column"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          {isUserCreator && (
            <button
              type="button"
              className="btn btn-link btn-sm text-danger p-1"
              onClick={() => onDeleteCustomColumn(column.id)}
              title={
                isFiltered
                  ? 'Cannot delete: column has active filters'
                  : 'Delete custom column'
              }
              disabled={isFiltered}
              style={{
                opacity: isFiltered ? 0.5 : 1,
                cursor: isFiltered ? 'not-allowed' : 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ColumnSelect({
  disabled = false,
  isUserCreator = true,
  params,
  setParams,
  sectionId,
  dashboardId,
  currentColumns,
  currentAnalytics,
  onOpenChange
}) {
  const [show, setShow] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [orderedColumns, setOrderedColumns] = useState([])
  const [editingCustomColumnId, setEditingCustomColumnId] = useState(null)
  const [editingCustomColumnName, setEditingCustomColumnName] = useState('')
  const [addingColumnId, setAddingColumnId] = useState(null)

  const queryClient = useQueryClient()
  const reorderColumnsMutation = useReorderDashboardColumns(dashboardId)
  const reorderAnalyticsColumnsMutation =
    useReorderDashboardAnalyticsColumns(dashboardId)
  const currentUser = useContext(UserContext)
  const token = useContext(AuthContext)
  // Prefixed onto the rollup labels — "OCAS: Org Rollup". Shown even where the district keeps
  // one grouping: naming it is what makes a rollup legible, not just what tells two apart.
  const groupingName = useActiveAccountGrouping(dashboardId)?.name || null

  // Mutations for batch column operations
  const createColumnMutation = useCreateDashboardColumn({
    params: { dashboard_id: dashboardId },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
      }
    }
  })

  const deleteColumnMutation = useDeleteDashboardColumn({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
      }
    }
  })

  // Reordering persists to the server immediately, but we defer the table-affecting
  // query refresh until the modal closes: dragging shouldn't churn the underlying table
  // mid-edit, and refetching before the PATCH commits is what caused the "one state
  // behind" order. (Add/remove still refresh live so the picker checkboxes stay in sync.)
  const reorderDirtyRef = React.useRef(false)
  const reorderSavesRef = React.useRef([])

  const handleClose = async () => {
    setShow(false)

    if (reorderDirtyRef.current) {
      const pendingSaves = reorderSavesRef.current
      reorderSavesRef.current = []
      reorderDirtyRef.current = false
      // Wait for any in-flight reorder PATCHes to commit before refetching, so the table
      // picks up the final order rather than a pre-commit snapshot.
      await Promise.allSettled(pendingSaves)
      queryClient.invalidateQueries([`dashboards/${dashboardId}`])
      queryClient.invalidateQueries(['customColumns', dashboardId])
    }

    // Signal closed last, so the parent re-enables the (heavy) transactions query
    // only after any deferred reorder invalidation has been queued. Add/remove edits
    // already invalidated the dashboard metadata live, so the table refetches once now.
    onOpenChange?.(false)
  }
  const handleShow = () => {
    onOpenChange?.(true)
    setShow(true)
  }

  const { isSuccess: isColumnsSuccess, data: columns } = useTableColumns({
    params: { section_id: sectionId },
    options: {}
  })

  const { isSuccess: isAnalyticsSuccess, data: analytics } =
    useAnalyticsColumns({
      params: { section_id: sectionId },
      options: {}
    })

  // Fetch custom columns for this dashboard
  const { data: customColumns = [] } = useQuery(
    ['customColumns', dashboardId],
    () => customColumnService.getCustomColumns(token, dashboardId),
    {
      enabled: !!dashboardId && !!token
    }
  )

  // Fetch custom columns by section for suggestions
  const { data: customColumnsBySection = [] } = useQuery(
    ['customColumnsBySection', currentUser.id],
    () => customColumnService.getCustomColumnsBySection(token, currentUser.id),
    {
      enabled: !!currentUser.id && !!token
    }
  )

  // Mutations for custom column operations
  const updateCustomColumnMutation = useMutation(
    ({ id, data }) => customColumnService.updateCustomColumn(token, id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customColumns', dashboardId])
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
        setEditingCustomColumnId(null)
        setEditingCustomColumnName('')
      }
    }
  )

  const deleteCustomColumnMutation = useMutation(
    (id) => customColumnService.deleteCustomColumn(token, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customColumns', dashboardId])
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
      }
    }
  )

  // Mutations for batch analytics column operations
  const createAnalyticsColumnMutation = useCreateDashboardAnalyticsColumn({
    params: { dashboard_id: dashboardId },
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
      }
    }
  })

  const deleteAnalyticsColumnMutation = useDeleteDashboardAnalyticsColumn({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
      }
    }
  })

  // Memoize custom columns to prevent unnecessary re-renders
  const memoizedCustomColumns = React.useMemo(
    () => customColumns,
    [JSON.stringify(customColumns)] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Get suggested custom columns from the same section (excluding current dashboard)
  const suggestedCustomColumns = useMemo(() => {
    if (!customColumnsBySection || !sectionId) return []

    // Find the section data that matches the current sectionId
    const currentSectionData = customColumnsBySection.find(
      (sectionData) => sectionData.section.id === sectionId
    )

    if (!currentSectionData) return []

    // Filter and deduplicate columns
    const filteredColumns = currentSectionData.custom_columns.filter((col) => {
      // Exclude if it's from current dashboard
      if (col.dashboard_id === dashboardId) return false

      // Exclude if a column with same name and type already exists in current dashboard
      const alreadyExists = memoizedCustomColumns.some(
        (existingCol) =>
          existingCol.name === col.name &&
          existingCol.column_type === col.column_type
      )

      return !alreadyExists
    })

    // Remove duplicates based on name and column_type combination
    const uniqueColumns = []
    const seen = new Set()

    filteredColumns.forEach((col) => {
      const key = `${col.name}-${col.column_type}`
      if (!seen.has(key)) {
        seen.add(key)
        uniqueColumns.push(col)
      }
    })

    return uniqueColumns
  }, [customColumnsBySection, sectionId, dashboardId, memoizedCustomColumns])

  // Initialize ordered columns based on current columns order, including custom columns and analytics
  React.useEffect(() => {
    if (isColumnsSuccess && columns && currentColumns) {
      const selectedColumns = currentColumns
        .map((cc) => columns.find((c) => c.id === cc.id))
        .filter(Boolean)

      // Use the backend-provided column_order for regular columns
      const selectedColumnsWithOrder = selectedColumns.map((col) => {
        const currentColumn = currentColumns.find((cc) => cc.id === col.id)
        return {
          ...col,
          order_position:
            currentColumn?.column_order !== null &&
            currentColumn?.column_order !== undefined
              ? currentColumn.column_order
              : 999 // Fallback for columns without order
        }
      })

      // Use the backend-provided column_order for custom columns
      const customColumnsWithType = memoizedCustomColumns.map((cc) => ({
        ...cc,
        database_column_name: `custom_${cc.id}`,
        name: cc.name,
        is_custom: true,
        order_position:
          cc.column_order !== null && cc.column_order !== undefined
            ? cc.column_order
            : 999 // Fallback for columns without order
      }))

      // Add selected analytics columns with is_analytics flag
      const selectedAnalytics = currentAnalytics
        ? currentAnalytics.map((analytic) => ({
            ...analytic,
            database_column_name: `analytics_${analytic.id}`,
            is_analytics: true,
            order_position:
              analytic.column_order !== null &&
              analytic.column_order !== undefined
                ? analytic.column_order
                : 999 // Fallback for analytics without order
          }))
        : []

      // Combine all columns and sort by their backend-provided order_position
      const allColumns = [
        ...selectedColumnsWithOrder,
        ...customColumnsWithType,
        ...selectedAnalytics
      ].sort((a, b) => {
        // Primary sort by order_position
        if (a.order_position !== b.order_position) {
          return a.order_position - b.order_position
        }
        // Secondary sort: regular columns before custom before analytics at same position
        if (a.is_custom !== b.is_custom) {
          return a.is_custom ? 1 : -1
        }
        if (a.is_analytics !== b.is_analytics) {
          return a.is_analytics ? 1 : -1
        }
        // Tertiary sort by creation order for same type
        return 0
      })

      // Ensure ThirdLine ID (is_row_id) is always first
      const idColumn = allColumns.find(
        (col) => col.is_row_id
      )
      const otherColumns = allColumns.filter(
        (col) => col.database_column_name !== 'id'
      )

      // Build final ordered list: ID first, then other columns
      if (idColumn) {
        setOrderedColumns([idColumn, ...otherColumns])
      } else {
        setOrderedColumns(otherColumns)
      }
    }
  }, [
    isColumnsSuccess,
    columns,
    currentColumns,
    memoizedCustomColumns,
    currentAnalytics
  ])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor)
  )

  const handleDragEnd = async (event) => {
    if (disabled) return

    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = orderedColumns.findIndex(
        (col) => col.id.toString() === active.id
      )
      const newIndex = orderedColumns.findIndex(
        (col) => col.id.toString() === over.id
      )

      if (oldIndex !== -1 && newIndex !== -1) {
        // Prevent moving other columns to position 0 if ThirdLine ID exists
        const hasThirdLineId = orderedColumns.some(
          (col) => col.is_row_id
        )
        if (hasThirdLineId && newIndex === 0) {
          return
        }

        const newOrder = arrayMove(orderedColumns, oldIndex, newIndex)

        // Ensure ThirdLine ID stays first if it exists
        const idColumn = newOrder.find(
          (col) => col.is_row_id
        )
        if (idColumn) {
          const idIndex = newOrder.indexOf(idColumn)
          if (idIndex !== 0) {
            // Move ThirdLine ID back to first position
            newOrder.splice(idIndex, 1)
            newOrder.unshift(idColumn)
          }
        }

        // Assign sequential order positions to ensure uniqueness across all columns
        const finalOrder = newOrder.map((col, index) => ({
          ...col,
          order_position: index
        }))

        setOrderedColumns(finalOrder)

        // Save column order to database
        if (dashboardId && currentColumns) {
          try {
            // Mark that a reorder happened so we refresh the table once, on close.
            reorderDirtyRef.current = true

            // Split regular columns, custom columns, and analytics columns from the final ordered list
            const regularColumns = finalOrder.filter(
              (col) => !col.is_custom && !col.is_analytics
            )
            const customColumnsInOrder = finalOrder.filter(
              (col) => col.is_custom
            )
            const analyticsColumnsInOrder = finalOrder.filter(
              (col) => col.is_analytics
            )

            // Update regular columns order with their sequential positions
            if (regularColumns.length > 0) {
              const regularColumnOrders = regularColumns
                .map((col) => {
                  const dashboardColumn = currentColumns.find(
                    (cc) => cc.id === col.id
                  )
                  if (!dashboardColumn) {
                    console.warn(
                      `Dashboard column not found for column ID: ${col.id}`
                    )
                    return null
                  }
                  return {
                    id: dashboardColumn.dashboard_column_id,
                    column_order: col.order_position // Use the sequential position
                  }
                })
                .filter(Boolean) // Remove null entries

              if (regularColumnOrders.length > 0) {
                // mutateAsync (not mutate) so we actually await the committed PATCH.
                const regularSave =
                  reorderColumnsMutation.mutateAsync(regularColumnOrders)
                reorderSavesRef.current.push(regularSave)
                await regularSave
              }
            }

            // Update custom columns order with their sequential positions
            if (customColumnsInOrder.length > 0) {
              const customColumnOrders = customColumnsInOrder.map((col) => {
                return {
                  id: col.id,
                  column_order: col.order_position // Use the sequential position
                }
              })

              const customSave = customColumnService.reorderCustomColumns(
                token,
                dashboardId,
                customColumnOrders
              )
              reorderSavesRef.current.push(customSave)
              await customSave
            }

            // Update analytics columns order with their sequential positions
            if (analyticsColumnsInOrder.length > 0) {
              const analyticsColumnOrders = analyticsColumnsInOrder
                .map((col) => {
                  const dashboardAnalyticsColumn = currentAnalytics?.find(
                    (ca) => ca.id === col.id
                  )
                  if (!dashboardAnalyticsColumn) {
                    console.warn(
                      `Dashboard analytics column not found for analytics ID: ${col.id}`
                    )
                    return null
                  }
                  return {
                    id: dashboardAnalyticsColumn.dashboard_analytics_column_id,
                    column_order: col.order_position // Use the sequential position
                  }
                })
                .filter(Boolean) // Remove null entries

              if (analyticsColumnOrders.length > 0) {
                const analyticsSave =
                  reorderAnalyticsColumnsMutation.mutateAsync(
                    analyticsColumnOrders
                  )
                reorderSavesRef.current.push(analyticsSave)
                await analyticsSave
              }
            }

            // Note: we intentionally do NOT invalidate here. The table refresh is
            // deferred to handleClose so reordering doesn't churn the table mid-edit
            // and we never refetch before the PATCH has committed.
          } catch (error) {
            console.error('Failed to update column order:', error)
            // Revert on error - reconstruct original order
            const selectedColumns = currentColumns
              .map((cc) => columns.find((c) => c.id === cc.id))
              .filter(Boolean)
            const customColumnsWithType = customColumns.map((cc) => ({
              ...cc,
              database_column_name: `custom_${cc.id}`,
              name: cc.name,
              is_custom: true
            }))
            const selectedAnalytics = currentAnalytics
              ? currentAnalytics.map((analytic) => ({
                  ...analytic,
                  database_column_name: `analytics_${analytic.id}`,
                  is_analytics: true
                }))
              : []
            const allColumns = [
              ...selectedColumns,
              ...customColumnsWithType,
              ...selectedAnalytics
            ]
            setOrderedColumns(allColumns)
          }
        }
      }
    }
  }

  const groupedColumns = useMemo(() => {
    if (!isColumnsSuccess) return []
    const groups = columns
      .filter((column) => column.is_selectable)
      .filter((column) => {
        if (!searchTerm) return true
        // Searched against the label as shown, so typing a grouping name finds its rollups.
        return getColumnDisplayName(
          column,
          currentUser.municipality,
          groupingName
        )
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      })
      .reduce((acc, col) => {
        const key = col.grouping || 'Other'
        if (!acc[key]) acc[key] = []
        acc[key].push(col)
        return acc
      }, {})
    Object.values(groups).forEach((cols) =>
      cols.sort((a, b) =>
        compareColumnsForDisplay(a, b, currentUser.municipality)
      )
    )
    let keys = Object.keys(groups).sort((a, b) => a.localeCompare(b))
    if (keys.includes('ThirdLine')) {
      keys = ['ThirdLine', ...keys.filter((k) => k !== 'ThirdLine')]
    }
    if (keys.includes('Calculated')) {
      keys = [...keys.filter((k) => k !== 'Calculated'), 'Calculated']
    }
    return keys.map((k) => ({ label: k, columns: groups[k] }))
  }, [
    isColumnsSuccess,
    columns,
    searchTerm,
    currentUser.municipality,
    groupingName
  ])

  const filteredAnalytics = useMemo(() => {
    if (!isAnalyticsSuccess || !analytics) return []
    if (!searchTerm) return analytics
    return analytics.filter((analytic) =>
      analytic.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [isAnalyticsSuccess, analytics, searchTerm])

  // Helper function to check if all columns in a group are selected
  const areAllColumnsInGroupSelected = (groupColumns) => {
    return groupColumns.every((column) =>
      currentColumns.some((cc) => cc.id === column.id)
    )
  }

  // Helper function to check if some columns in a group are selected
  const areSomeColumnsInGroupSelected = (groupColumns) => {
    return groupColumns.some((column) =>
      currentColumns.some((cc) => cc.id === column.id)
    )
  }

  // Handler for select all/none in a group
  const handleSelectAllGroup = (groupColumns, isChecked) => {
    if (isChecked) {
      // Get IDs of columns that are not already selected
      const columnsToAdd = groupColumns.filter(
        (column) => !currentColumns.some((cc) => cc.id === column.id)
      )

      if (columnsToAdd.length > 0) {
        const tableColumnIds = columnsToAdd.map((column) => column.id)

        // Use the batch create mutation with table_column_ids
        createColumnMutation.mutate({
          dashboard_id: dashboardId,
          table_column_ids: tableColumnIds
        })
      }
    } else {
      // Get IDs of dashboard columns to delete, excluding filtered columns
      const columnsToDelete = currentColumns.filter((cc) => {
        const matchingColumn = groupColumns.find(
          (column) => column.id === cc.id
        )
        if (!matchingColumn) return false
        // Don't delete if the column is filtered
        return !isColumnFiltered(matchingColumn, params)
      })

      if (columnsToDelete.length > 0) {
        const dashboardColumnIds = columnsToDelete.map(
          (cc) => cc.dashboard_column_id
        )

        // Use batch delete - pass ids array
        deleteColumnMutation.mutate(dashboardColumnIds)
      }
    }
  }

  // Helper functions for analytics selection
  const areAllAnalyticsSelected = () => {
    if (!filteredAnalytics || filteredAnalytics.length === 0) return false
    return filteredAnalytics.every((analytic) =>
      currentAnalytics?.some((ca) => ca.id === analytic.id)
    )
  }

  const areSomeAnalyticsSelected = () => {
    if (!filteredAnalytics || filteredAnalytics.length === 0) return false
    return filteredAnalytics.some((analytic) =>
      currentAnalytics?.some((ca) => ca.id === analytic.id)
    )
  }

  // Handler for select all/none analytics
  const handleSelectAllAnalytics = (isChecked) => {
    if (!filteredAnalytics || filteredAnalytics.length === 0) return

    if (isChecked) {
      // Get IDs of analytics that are not already selected
      const analyticsToAdd = filteredAnalytics.filter(
        (analytic) => !currentAnalytics?.some((ca) => ca.id === analytic.id)
      )

      if (analyticsToAdd.length > 0) {
        const analyticIds = analyticsToAdd.map((analytic) => analytic.id)

        // Use the batch create mutation with analytic_ids
        createAnalyticsColumnMutation.mutate({
          dashboard_id: dashboardId,
          analytic_ids: analyticIds
        })
      }
    } else {
      // Get IDs of dashboard analytics columns to delete, excluding filtered columns
      const analyticsToDelete =
        currentAnalytics?.filter((ca) => {
          const matchingAnalytic = filteredAnalytics.find(
            (analytic) => analytic.id === ca.id
          )
          if (!matchingAnalytic) return false
          // Don't delete if the analytics column is filtered
          return !isAnalyticsColumnFiltered(matchingAnalytic, params)
        }) || []

      if (analyticsToDelete.length > 0) {
        const dashboardAnalyticsColumnIds = analyticsToDelete
          .filter((a) => a.dashboard_analytics_column_id)
          .map((a) => a.dashboard_analytics_column_id)

        // Use batch delete - pass ids array
        if (dashboardAnalyticsColumnIds.length > 0) {
          deleteAnalyticsColumnMutation.mutate(dashboardAnalyticsColumnIds)
        }
      }
    }
  }

  // Custom column handlers
  const handleEditCustomColumn = (column) => {
    setEditingCustomColumnId(column.id)
    setEditingCustomColumnName(column.name)
  }

  const handleSaveEditCustomColumn = () => {
    if (editingCustomColumnName.trim()) {
      updateCustomColumnMutation.mutate({
        id: editingCustomColumnId,
        data: { name: editingCustomColumnName.trim() }
      })
    }
  }

  const handleCancelEditCustomColumn = () => {
    setEditingCustomColumnId(null)
    setEditingCustomColumnName('')
  }

  const handleDeleteCustomColumn = (columnId) => {
    // Check if the custom column is filtered
    const customColumn = memoizedCustomColumns.find((cc) => cc.id === columnId)
    if (customColumn && isCustomColumnFiltered(customColumn, params)) {
      alert(
        'This custom column cannot be deleted because it has active filters applied. Please clear the filters first.'
      )
      return
    }

    if (
      window.confirm(
        'Are you sure you want to delete this custom column? All data in this column will be lost.'
      )
    ) {
      deleteCustomColumnMutation.mutate(columnId)
    }
  }

  const createCustomColumnFromSuggestionMutation = useMutation(
    (data) => customColumnService.createCustomColumn(token, dashboardId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customColumns', dashboardId])
        queryClient.invalidateQueries([
          'customColumnsBySection',
          currentUser.id
        ])
        setAddingColumnId(null)
      },
      onError: () => {
        setAddingColumnId(null)
      }
    }
  )

  const handleAddSuggestedColumn = (suggestedColumn) => {
    // Set the loading state for this specific column
    setAddingColumnId(suggestedColumn.id)

    // Create a new custom column based on the suggested one
    const columnData = {
      name: suggestedColumn.name,
      column_type: suggestedColumn.column_type,
      is_active: true,
      settings: suggestedColumn.settings || {}
    }

    createCustomColumnFromSuggestionMutation.mutate(columnData)
  }

  const formatColumnType = (columnType) => {
    if (columnType === 'boolean') {
      return 'Checkbox'
    }
    return columnType.charAt(0).toUpperCase() + columnType.slice(1)
  }

  return (
    <React.Fragment>
      <Button
        className="text-dark outline-dark bg-light me-2"
        variant="light"
        data-testid="column-select"
        onClick={handleShow}
        size="sm"
      >
        <FontAwesomeIcon icon={faColumns} className="me-2" />
        Columns
      </Button>
      <Modal show={show} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Manage Columns</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '600px' }}>
          <Row>
            <Col md={6}>
              <h5 className="mb-3">Available Columns</h5>
              <div className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Search columns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="text-muted small mt-1">
                  <FontAwesomeIcon icon={faSearch} className="me-1" />
                  Search through all available columns and analytics
                </div>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {isColumnsSuccess &&
                  groupedColumns.map((group) => {
                    const allSelected = areAllColumnsInGroupSelected(
                      group.columns
                    )
                    const someSelected = areSomeColumnsInGroupSelected(
                      group.columns
                    )

                    return (
                      <div key={`group-${group.label}`}>
                        <div className="d-flex align-items-center mt-3 mb-1">
                          <Form.Check
                            type="checkbox"
                            id={`group-select-${group.label}`}
                            checked={allSelected}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate =
                                  !allSelected && someSelected
                              }
                            }}
                            onChange={(e) =>
                              handleSelectAllGroup(
                                group.columns,
                                e.target.checked
                              )
                            }
                            disabled={disabled}
                            className="me-2"
                          />
                          <label
                            htmlFor={`group-select-${group.label}`}
                            className="fw-bold mb-0"
                            style={{ cursor: disabled ? 'default' : 'pointer' }}
                          >
                            {group.label}
                          </label>
                        </div>
                        <div className="ms-4">
                          {group.columns.map((column) => (
                            <UpdateColumn
                              key={`column-${column.id}`}
                              column={column}
                              dashboardId={dashboardId}
                              groupingName={groupingName}
                              currentColumns={currentColumns}
                              disabled={disabled}
                              filterParams={params}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  })}
                <div key="group-analytics">
                  <div className="d-flex align-items-center mt-3 mb-1">
                    <Form.Check
                      type="checkbox"
                      id="analytics-select-all"
                      checked={areAllAnalyticsSelected()}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate =
                            !areAllAnalyticsSelected() &&
                            areSomeAnalyticsSelected()
                        }
                      }}
                      onChange={(e) =>
                        handleSelectAllAnalytics(e.target.checked)
                      }
                      disabled={
                        disabled ||
                        !isAnalyticsSuccess ||
                        !filteredAnalytics ||
                        filteredAnalytics.length === 0
                      }
                      className="me-2"
                    />
                    <label
                      htmlFor="analytics-select-all"
                      className="fw-bold mb-0"
                      style={{
                        cursor:
                          disabled ||
                          !isAnalyticsSuccess ||
                          !filteredAnalytics ||
                          filteredAnalytics.length === 0
                            ? 'default'
                            : 'pointer'
                      }}
                    >
                      Analytics
                    </label>
                  </div>
                  <div className="ms-4">
                    {isAnalyticsSuccess ? (
                      filteredAnalytics && filteredAnalytics.length > 0 ? (
                        filteredAnalytics.map((analytic) => (
                          <UpdateAnalyticsColumn
                            key={`analytics-${analytic.id}`}
                            analytic={analytic}
                            dashboardId={dashboardId}
                            currentAnalytics={currentAnalytics || []}
                            disabled={disabled}
                            filterParams={params}
                          />
                        ))
                      ) : (
                        <p className="text-muted">
                          {searchTerm
                            ? 'No analytics match your search'
                            : 'No analytics available for this section'}
                        </p>
                      )
                    ) : (
                      <p className="text-muted">Loading analytics...</p>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-4">
                <CustomColumnsSection
                  dashboardId={dashboardId}
                  disabled={disabled}
                />
              </div>

              {/* Suggested Custom Columns Section */}
              {suggestedCustomColumns.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Suggested Custom Columns</h6>
                  <div className="text-muted small mb-3">
                    Custom columns from other dashboards that you might want to
                    add:
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {suggestedCustomColumns.map((suggestedColumn) => (
                      <div
                        key={`suggested-${suggestedColumn.id}`}
                        className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded bg-light"
                      >
                        <div className="flex-grow-1">
                          <span className="fw-medium">
                            {suggestedColumn.name}
                          </span>
                          <span className="text-muted small ms-2">
                            ({formatColumnType(suggestedColumn.column_type)})
                          </span>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={
                            disabled || addingColumnId === suggestedColumn.id
                          }
                          onClick={() =>
                            handleAddSuggestedColumn(suggestedColumn)
                          }
                          title="Add this custom column to current dashboard"
                        >
                          {addingColumnId === suggestedColumn.id
                            ? 'Adding...'
                            : 'Add'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h5 className="mb-3">Column Order</h5>
              <div className="text-muted small mb-3">
                <FontAwesomeIcon icon={faGripVertical} className="me-1" />
                Drag columns to reorder them in the table
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {orderedColumns.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={orderedColumns.map((col) => col.id.toString())}
                      strategy={verticalListSortingStrategy}
                    >
                      {orderedColumns.map((column) => (
                        <SortableColumnItem
                          key={column.id}
                          id={column.id.toString()}
                          column={column}
                          municipality={currentUser.municipality}
                          groupingName={groupingName}
                          onEditCustomColumn={handleEditCustomColumn}
                          onDeleteCustomColumn={handleDeleteCustomColumn}
                          editingCustomColumnId={editingCustomColumnId}
                          editingCustomColumnName={editingCustomColumnName}
                          setEditingCustomColumnName={
                            setEditingCustomColumnName
                          }
                          onSaveEditCustomColumn={handleSaveEditCustomColumn}
                          onCancelEditCustomColumn={
                            handleCancelEditCustomColumn
                          }
                          disabled={disabled}
                          params={params}
                          isUserCreator={isUserCreator}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="text-muted text-center p-4">
                    <p>No columns selected</p>
                    <p className="small">
                      Select columns from the left panel to see them here
                    </p>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}

export default ColumnSelect
